import { FC, useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash-es';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { PresenceChannel } from '@ably/laravel-echo';
import { Box, Button, Flex, Spinner, Stack, Tag, Text } from '@chakra-ui/react';

import {
  useApplicationWorkbook,
  useUpdateApplicationWorkbook,
} from '~/core/shared/api/middle-office/application';
import { FormField, Wysiwyg } from '~/core/shared/components';
import { useUserData } from '~/core/shared/contexts/UserContext';
import { useWebsocketConnection } from '~/core/shared/contexts/websockets';
import { Permissions } from '~/core/shared/generated/Permissions';
import { useFormResolver } from '~/core/shared/hooks';
import { useCan } from '~/core/shared/hooks/useCan';
import { FormFieldUpdateObject, Nullable } from '~/core/shared/types';
import { handleError } from '~/core/shared/utils/admin';
import { logger } from '~/core/shared/utils/logger';

type WorkbookProps = {
  documentId: string;
  documentDefinition: string;
  cancel: () => void;
};

type WorkbookFormValues = {
  content: string;
};

type WorkbookChannelUser = {
  id: string;
  name: string;
  canEdit: boolean;
};

type ContentUpdateEvent = {
  user: WorkbookChannelUser;
  content: string;
};

type EditorStatusChangeEvent = {
  user: WorkbookChannelUser;
};

const validationSchema: yup.ObjectSchema<WorkbookFormValues> = yup.object({
  content: yup.string().required(),
});

export const Workbook: FC<WorkbookProps> = ({
  cancel,
  documentId,
  documentDefinition,
}) => {
  const { user } = useUserData();
  const { canAll } = useCan();
  const echo = useWebsocketConnection();
  const { workbook, isLoading: isLoadingInitialContent } =
    useApplicationWorkbook(documentId, documentDefinition);
  const workbookContent = workbook?.content;

  const [isLocked, setIsLocked] = useState<boolean>(false);
  const [channel, setChannel] = useState<PresenceChannel>();
  const [editor, setEditor] = useState<WorkbookChannelUser>();
  const [viewers, setViewers] = useState<WorkbookChannelUser[]>([]);

  const hasPermssionToUpdate = canAll([Permissions.WorkbooksUpdate]);
  const isEditable = !isLocked && hasPermssionToUpdate;

  const releaseEditorTimeoutID = useRef<number | undefined>(undefined);
  useEffect(() => () => clearTimeout(releaseEditorTimeoutID.current), []);

  const { updateWorkbook } = useUpdateApplicationWorkbook(
    documentId,
    documentDefinition,
  );

  const currentWorkbookUser: Nullable<WorkbookChannelUser> = user
    ? {
        id: user.id,
        name: user.name,
        canEdit: hasPermssionToUpdate,
      }
    : null;

  const defaultValues: WorkbookFormValues = {
    content: workbookContent || '',
  };

  // TODO: @haxxxton.. are we even properly using a form here?? this looks like its autosaving, with no use of the validation schema
  const resolver = useFormResolver<WorkbookFormValues>(validationSchema);
  const form = useForm<WorkbookFormValues>({
    defaultValues,
    resolver,
  });

  const formWorkbookContent = form.watch('content');

  const notifyEditorLockRelease = (): void => {
    if (channel && currentWorkbookUser) {
      channel.whisper('releaseEditorLock', {
        user: currentWorkbookUser,
      });
    }
  };

  const releaseEditorLock = (): void => {
    logger.debug('releaseEditorLock');
    setIsLocked(false);
    setEditor(undefined);
  };

  const releaseEditorLockReservedForUser = (
    editorUser: WorkbookChannelUser,
  ): void => {
    logger.debug('releaseEditorLockReservedForUser', {
      editor,
      user: editorUser,
    });
    releaseEditorLock();
  };

  const releaseEditorLockAndNotify = (): void => {
    if (currentWorkbookUser) {
      releaseEditorLockReservedForUser(currentWorkbookUser);
      notifyEditorLockRelease();
    }
  };

  const setEditorReleaseTimeout = (): void => {
    if (releaseEditorTimeoutID.current) {
      clearTimeout(releaseEditorTimeoutID.current);
    }
    releaseEditorTimeoutID.current = setTimeout(() => {
      releaseEditorLockAndNotify();
    }, 1000) as any;
  };

  const notifyContentUpdate = (content: string): void => {
    if (isLocked) {
      console.error('cannot make changes when not in editing mode');
      return;
    }
    logger.debug('notifyContentUpdate', { content });
    if (channel) {
      channel.whisper('contentUpdate', {
        content,
        user: currentWorkbookUser,
      });
    }
  };

  const updateFormContent = (content: string): void => {
    form.setValue('content', content);
  };

  useEffect(() => {
    if (workbookContent) {
      updateFormContent(workbookContent);
    }
  }, [workbookContent]);

  const reserveEditorLock = (selectedUser: WorkbookChannelUser): void => {
    setIsLocked(currentWorkbookUser?.id !== selectedUser.id);
    logger.debug('reserveEditorLock', selectedUser);
    setEditor(selectedUser);
  };

  const onTextChange = debounce((value: FormFieldUpdateObject<any>): void => {
    const isChangedText = formWorkbookContent !== value.value;
    const isNotDefaultText = value.value !== defaultValues.content;
    const isAllowedToEdit =
      (!editor || editor.id === currentWorkbookUser?.id) &&
      currentWorkbookUser?.canEdit;
    if (isChangedText && isNotDefaultText && isAllowedToEdit) {
      logger.debug('text updated', {
        form: formWorkbookContent,
        value: value.value,
      });
      updateWorkbook({
        content: value.value,
      });
      if (currentWorkbookUser) {
        logger.debug('setEditorOnTextChange', currentWorkbookUser);
        setEditor(currentWorkbookUser);
        setEditorReleaseTimeout();
      }
      notifyContentUpdate(value.value);
    }
  }, 500);

  const onSubmit = async (formValues: WorkbookFormValues): Promise<void> => {
    updateWorkbook(formValues);
  };

  const listen = async (): Promise<void> => {
    if (!echo) return;
    logger.debug('WORKBOOK echo instance', { echo });
    const workbookChannel = echo.join(`workbook.${documentId}`);
    setChannel(workbookChannel);

    if (workbookChannel) {
      workbookChannel
        .here((users: WorkbookChannelUser[]) => {
          setViewers(users);
        })
        .joining((channelUser: WorkbookChannelUser) => {
          setViewers((prevViewers) =>
            ([] as WorkbookChannelUser[]).concat(
              prevViewers.filter((viewer) => viewer.id !== channelUser.id),
              [channelUser],
            ),
          );
        })
        .leaving((u: WorkbookChannelUser) => {
          setViewers((prevViewers) => {
            const updated = prevViewers.filter((viewer) => viewer.id !== u.id);
            return updated;
          });
          releaseEditorLockReservedForUser(u);
        })
        .error((error: any) => {
          handleError('Could not establish websocket connection');
          console.error(error);
        })
        .listenForWhisper('releaseEditorLock', (e: EditorStatusChangeEvent) => {
          logger.debug('whisper: releaseEditorLock', e);
          releaseEditorLockReservedForUser(e.user);
        })
        .listenForWhisper('contentUpdate', (e: ContentUpdateEvent) => {
          logger.debug('whisper: contentUpdate', e);
          if (e.user.id !== currentWorkbookUser?.id && e.user.canEdit) {
            reserveEditorLock(e.user);
            updateFormContent(e.content);
          }
        });
    }
  };

  const disconnect = (): void => {
    if (!echo) return;
    logger.debug('disconnect');
    echo.leaveChannel(`presence:workbook.${documentId}`);
    // echo.disconnect();
    if (channel) {
      setChannel(undefined);
    }
  };

  useEffect(() => {
    listen();
    return () => {
      disconnect();
    };
  }, [echo]);

  const scopedStyles = `
    .ck-editor__editable_inline {
      min-height: 500px;
      max-width: 478px;
    }

    .ck-editor__editable_inline a {
      color: blue;
      text-decoration: underline;
    }
  `;

  return (
    <Flex direction="column" minH="40vh" minW="500px">
      <style>{scopedStyles}</style>
      {isLoadingInitialContent && <Spinner />}
      {!isLoadingInitialContent && (
        <>
          <Text my={5}>Store internal updates to an application.</Text>

          <FormProvider {...form}>
            <Stack as="form" gap={4} onSubmit={form.handleSubmit(onSubmit)}>
              <FormField
                borderColor="gray.300"
                component={Wysiwyg}
                hooks={{
                  afterOnBlur: releaseEditorLockAndNotify,
                  afterOnChange: onTextChange,
                }}
                isLocked={!isEditable}
                label="Content"
                name="content"
                required
                size="md"
                toolbarItems={[
                  'bulletedList',
                  'numberedList',
                  'bold',
                  'underline',
                  'link',
                ]}
                variant="outline"
              />

              {isEditable && <Text color="green">Editing is enabled.</Text>}
              {!isEditable && <Text color="orange">Editing is disabled.</Text>}

              <Box>
                Viewing:{' '}
                {viewers.map((viewer, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Tag.Root key={i} colorPalette="primary" mr={1}>
                    <Tag.Label>{viewer.name}</Tag.Label>
                  </Tag.Root>
                ))}
                {viewers.length === 0 && (
                  <Tag.Root>
                    <Tag.Label>nobody</Tag.Label>
                  </Tag.Root>
                )}
              </Box>

              <Box mr="4">
                Editing:{' '}
                {editor && (
                  <Tag.Root colorPalette="orange">
                    <Tag.Label>{editor.name}</Tag.Label>
                  </Tag.Root>
                )}
                {!editor && (
                  <Tag.Root>
                    <Tag.Label>nobody</Tag.Label>
                  </Tag.Root>
                )}
              </Box>

              <Stack direction="row" justifyContent="end" mt={3}>
                <Button
                  colorPalette="gray"
                  fontSize="md"
                  fontWeight="normal"
                  onClick={cancel}
                  variant="outline"
                >
                  Close
                </Button>
                {/* <Button colorPalette="primary" fontSize="md" fontWeight="bold" type="submit" variant="solid">
                  Save
                </Button> */}
              </Stack>
            </Stack>
          </FormProvider>
        </>
      )}
    </Flex>
  );
};
